/**
 * SoulogGhostCursor — the green "demo cursor" shared by the homepage try-it sections (user request 2026-09-28;
 * cursor art Figma 1193:914 "等待", styles in assets/soulog-ghost-cursor.css).
 *
 *   · While the visitor's pointer is outside the section's zone, a demo cursor (.sgc-ghost) rests on the button we want clicked,
 *     sways left and right, and follows that button when it changes (the section calls park()).
 *   · When the pointer enters the zone, the demo cursor flies to the pointer (chasing it every frame), and on arrival it disappears
 *     and the system cursor becomes the same art (data-sgc-on on the host) — same image, same hotspot, so the two read as merging.
 *   · Leaving the zone restores the system cursor and brings the demo cursor back to its button.
 *
 * Desktop mouse only (CURSOR_MQ). The width gate is required: the theme editor's mobile preview is a narrow viewport driven
 * by a desktop mouse, so a pointer-only gate would wrongly match there.
 *
 * Used by: assets/soulog-home-record.js (the lit chip), assets/soulog-home-rewind.js (the "Enabled" button on the choice card),
 *          assets/soulog-pdp-rewind.js (the product page Rewind demo button, once it reads "Rewound to …").
 * Each section includes this script before its own; the guard keeps a second copy on the page from redefining it.
 *
 *   new SoulogGhostCursor(host, {
 *     container,   // element the demo cursor is placed in (positioned; it scrolls with the button)
 *     target,      // () => the element to rest on, or null to hide the demo cursor
 *     zone,        // () => { left, right, top, bottom } in viewport px — entering it merges the cursors
 *   })
 *   .park()          re-read target() and slide there (call when the target changes)
 *   .styleFrame(f)   same-origin iframe inside the zone: show the green cursor in it too
 *   .destroy()
 */
if (!window.SoulogGhostCursor) {
	const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
	const CURSOR_MQ =
		"(min-width: 1024px) and (orientation: landscape) and (hover: hover) and (pointer: fine), (min-width: 1367px) and (hover: hover) and (pointer: fine)";
	/** Demo cursor flight time to the visitor's pointer */
	const FLY_MS = 450;
	/** Where the tip rests on the target (Figma 1193:558: ~62% across, on the bottom edge) */
	const REST_AT = { x: 0.62, dy: -2 };

	const bindMq = (mq, fn) => {
		if (mq.addEventListener) mq.addEventListener("change", fn);
		else if (mq.addListener) mq.addListener(fn);
	};
	const unbindMq = (mq, fn) => {
		if (mq.removeEventListener) mq.removeEventListener("change", fn);
		else if (mq.removeListener) mq.removeListener(fn);
	};

	class SoulogGhostCursor {
		/** Is the point inside the zone? */
		static inZone(x, y, zone) {
			return !!zone && x >= zone.left && x <= zone.right && y >= zone.top && y <= zone.bottom;
		}

		/** Eased position at time fraction t, from `from` toward `to` (`to` may move every frame — it chases the pointer) */
		static flyStep(from, to, t) {
			const u = clamp(t, 0, 1);
			const e = u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2;
			return { x: from.x + (to.x - from.x) * e, y: from.y + (to.y - from.y) * e };
		}

		/** Bounding box of two rects — for a zone made of several cells (e.g. a card drawn by a pseudo-element) */
		static union(a, b) {
			return {
				left: Math.min(a.left, b.left),
				right: Math.max(a.right, b.right),
				top: Math.min(a.top, b.top),
				bottom: Math.max(a.bottom, b.bottom),
			};
		}

		/** Tip rest point for a target rect, relative to the container rect */
		static restFor(target, box) {
			return { x: target.left - box.left + target.width * REST_AT.x, y: target.bottom - box.top + REST_AT.dy };
		}

		constructor(host, { container, target, zone }) {
			this.host = host;
			this.box = container;
			this.target = target;
			this.zone = zone;
			this.merged = false;
			this.flying = false;
			this.pointer = null;
			this.pos = null;
			this.mq = window.matchMedia(CURSOR_MQ);
			this.reduceMq = window.matchMedia("(prefers-reduced-motion: reduce)");

			if (this.box) {
				this.ghost = document.createElement("span");
				this.ghost.className = "sgc-ghost";
				this.ghost.setAttribute("aria-hidden", "true");
				this.ghost.hidden = true;
				this.ghost.innerHTML = '<span class="sgc-ghost-i"></span>';
				this.box.appendChild(this.ghost);
			}

			this.onMove = (e) => {
				this.pointer = { x: e.clientX, y: e.clientY };
				this.check();
			};
			this.onLeave = () => {
				this.pointer = null;
				this.check();
			};
			this.onScroll = () => this.check();
			this.onResize = () => this.park();
			this.onMode = () => this.setMode();
			bindMq(this.mq, this.onMode);
			window.addEventListener("resize", this.onResize);
			this.setMode();
		}

		destroy() {
			this.endFlight();
			this.unbindPointer();
			unbindMq(this.mq, this.onMode);
			window.removeEventListener("resize", this.onResize);
			this.host.removeAttribute("data-sgc-mode");
			this.host.removeAttribute("data-sgc-on");
			this.merged = false;
			this.syncFrames();
			if (this.ghost) this.ghost.remove();
		}

		setMode() {
			this.on = this.mq.matches && !!this.ghost;
			this.host.toggleAttribute("data-sgc-mode", this.on);
			this.unbindPointer();
			if (this.on) {
				window.addEventListener("pointermove", this.onMove, { passive: true });
				window.addEventListener("scroll", this.onScroll, { passive: true });
				document.documentElement.addEventListener("mouseleave", this.onLeave);
			} else this.unmerge();
			this.park();
		}

		unbindPointer() {
			window.removeEventListener("pointermove", this.onMove);
			window.removeEventListener("scroll", this.onScroll);
			document.documentElement.removeEventListener("mouseleave", this.onLeave);
		}

		/** Where the tip should rest now (container coordinates), or null when there's nothing to point at */
		restPoint() {
			const el = this.target();
			if (!el || !this.box || !el.getClientRects().length) return null;
			return SoulogGhostCursor.restFor(el.getBoundingClientRect(), this.box.getBoundingClientRect());
		}

		place(p) {
			this.pos = p;
			this.ghost.style.setProperty("--sgc-x", p.x + "px");
			this.ghost.style.setProperty("--sgc-y", p.y + "px");
		}

		/** Slide the demo cursor onto the current target (CSS transition); hidden while merged or with nothing to point at */
		park() {
			if (!this.ghost || this.flying) return;
			const p = this.on && !this.merged ? this.restPoint() : null;
			if (p) {
				// First appearance: jump there instead of sliding in from the container's corner
				if (this.ghost.hidden) this.ghost.setAttribute("data-flying", "");
				this.place(p);
				// (unless a flight started meanwhile — dropping data-flying mid-flight would put the slow transform transition back on)
				if (this.ghost.hidden)
					requestAnimationFrame(() => this.ghost && !this.flying && this.ghost.removeAttribute("data-flying"));
			}
			this.ghost.hidden = !p;
			// The target can appear under a pointer that's already resting in the zone (e.g. the first chip lights up after the
			// scroll has stopped): with no move or scroll to come, nothing else would ever merge it (2026-09-29 user)
			if (p && this.pointer) this.check();
		}

		/**
		 * Pointer moved, the page scrolled under it, or the target changed: merge on entering the zone, split again on leaving.
		 * No target (e.g. Never Miss with its choice card closed) = nothing to demo, so the zone is inactive (2026-09-29 user).
		 */
		check() {
			if (!this.on) return;
			const inside =
				this.pointer && !!this.target() && SoulogGhostCursor.inZone(this.pointer.x, this.pointer.y, this.zone());
			if (inside && !this.merged && !this.flying) this.fly();
			else if (!inside && (this.merged || this.flying)) this.unmerge();
		}

		/** Fly from the rest point to the visitor's pointer, then swap in the green system cursor */
		fly() {
			const from = this.pos;
			if (!from || this.ghost.hidden || this.reduceMq.matches) return this.merge();
			this.flying = true;
			this.ghost.setAttribute("data-flying", "");
			const t0 = performance.now();
			const frame = (now) => {
				if (!this.flying) return;
				const t = (now - t0) / FLY_MS;
				const b = this.box.getBoundingClientRect();
				const to = this.pointer ? { x: this.pointer.x - b.left, y: this.pointer.y - b.top } : from;
				this.place(SoulogGhostCursor.flyStep(from, to, t));
				if (t >= 1) this.merge();
				else this.raf = requestAnimationFrame(frame);
			};
			this.raf = requestAnimationFrame(frame);
		}

		endFlight() {
			cancelAnimationFrame(this.raf);
			this.flying = false;
			if (this.ghost) this.ghost.removeAttribute("data-flying");
		}

		merge() {
			if (this.ghost) this.ghost.hidden = true;
			this.endFlight();
			this.merged = true;
			this.host.toggleAttribute("data-sgc-on", true);
			this.syncFrames();
		}

		unmerge() {
			const wasFlying = this.flying;
			this.endFlight();
			this.merged = false;
			this.host.toggleAttribute("data-sgc-on", false);
			this.syncFrames();
			// Cut off mid-flight: jump straight back instead of sliding back from wherever it was
			if (wasFlying && this.ghost) this.ghost.hidden = true;
			requestAnimationFrame(() => this.park());
		}

		/**
		 * A same-origin iframe inside the zone (e.g. the Recap page, a Blob URL): its cursor is the frame's own, so give it the same
		 * green rule, switched by data-sgc-on on the frame's <html> in step with the page (syncFrames) — green only while merged.
		 * Pointer moves over the frame never reach this page; that's fine, it can only be reached through the zone around it.
		 * Call again after the frame loads a new document (the style lives in the document).
		 */
		styleFrame(frame) {
			if (!this.on) return;
			try {
				const doc = frame.contentDocument;
				if (!doc || !doc.head) return;
				if (!this.frames) this.frames = [];
				if (this.frames.indexOf(frame) < 0) this.frames.push(frame);
				if (doc.getElementById("sgc-cursor")) return this.syncFrames();
				// Same bitmap cursor as the page (1x + 2x PNG, see the CSS for why not the SVG)
				const cs = getComputedStyle(this.host);
				const png1 = cs.getPropertyValue("--sgc-png1").trim();
				const png2 = cs.getPropertyValue("--sgc-png2").trim();
				if (!png1 || !png2) return;
				const st = doc.createElement("style");
				st.id = "sgc-cursor";
				st.textContent =
					"html[data-sgc-on], html[data-sgc-on] * { cursor: " +
					png1 +
					" 21 18, auto !important; }" +
					'@supports (cursor: image-set(url("x.png") 1x) 0 0, auto) { html[data-sgc-on], html[data-sgc-on] * { cursor: image-set(' +
					png1 +
					" 1x, " +
					png2 +
					" 2x) 21 18, auto !important; } }";
				doc.head.appendChild(st);
				this.syncFrames();
			} catch (e) {
				/* not same-origin: leave the frame's own cursor */
			}
		}

		/** Mirror the merged state onto every styled frame */
		syncFrames() {
			(this.frames || []).forEach((f) => {
				try {
					const de = f.contentDocument && f.contentDocument.documentElement;
					if (de) de.toggleAttribute("data-sgc-on", this.merged);
				} catch (e) {
					/* frame navigated cross-origin */
				}
			});
		}
	}

	window.SoulogGhostCursor = SoulogGhostCursor;
}
